import { useZero } from "@rocicorp/zero/react";
import { useCallback } from "react";
import { makeBranchParams } from "../../shared/copyOnWrite";
import { queries } from "../../shared/queries";
import { navigate, routes } from "../lib/urls";
import type { ZeroResult } from "../lib/zeroResultError";
import { trackOptimisticBranch } from "./useBranchServerReady";
import { useQuery } from "./useQuery";

export function useNavigateOnBranchChange(
  branchId: string,
  // When the caller isn't already on the workflow page (e.g. the connector
  // modal opened from the apps directory), pass the workflow so navigation can
  // build a complete URL instead of inheriting tokens from the current route.
  workflow?: { spaceId: string; workflowId: string }
) {
  const z = useZero();
  const spaceId = workflow?.spaceId;
  const workflowId = workflow?.workflowId;
  // Editing only forks when on "main" (see ensureWritable). When the current
  // branch is loaded and isn't main, there's no fork to detect, so skip the
  // round-trip and return immediately — the caller (e.g. opening the connector
  // modal) pays no latency.
  const [branch] = useQuery(queries.loadBranchSummary({ branchId }));
  const cannotFork = branch != null && branch.name !== "main";

  return useCallback(
    async (
      run: (branchParams: ReturnType<typeof makeBranchParams>) => {
        client: Promise<unknown>;
        server: Promise<ZeroResult>;
      }
    ): Promise<string | null> => {
      const branchParams = makeBranchParams(branchId);
      const result = run(branchParams);
      await result.client;
      if (cannotFork) return null;
      const forkBranch = await z.run(
        queries.loadBranchSummary({
          branchId: branchParams.forkBranchId,
        })
      );
      if (!forkBranch) return null;

      trackOptimisticBranch(branchParams.forkBranchId, result.server, () =>
        navigate(
          routes.space.workflow,
          {
            ...(spaceId && workflowId ? { spaceId, workflowId } : {}),
            branchId,
            commitId: "",
          },
          { replace: true }
        )
      );

      navigate(routes.space.workflow, {
        ...(spaceId && workflowId ? { spaceId, workflowId } : {}),
        branchId: branchParams.forkBranchId,
        commitId: "",
      });
      return branchParams.forkBranchId;
    },
    [z, branchId, cannotFork, spaceId, workflowId]
  );
}
